import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  CrearEvento,
  EditarEvento,
  EstadoEvento,
  EventoCalendario,
  PropiedadResumen,
  TipoEvento,
} from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';

interface Props {
  idioma: Idioma;
  onVolver: () => void;
  onAbrirPropiedad: (id: string) => void;
}

const TIPOS: TipoEvento[] = ['VISITA', 'PREVENTIVA', 'REVISION', 'LLEGADA', 'SALIDA', 'OTRO'];

/*
  Seis tipos, seis TONOS distintos. Antes cuatro de los seis caian en la gama
  calida —dorado, ambar y dos grises tostados— y el mes entero se leia del mismo
  color: el punto del filtro dejaba de decir nada. El azul del sistema entra en
  mantenimiento y el navy en la salida para abrir el abanico.
*/
const COLOR: Record<TipoEvento, string> = {
  VISITA: 'var(--t-acento)',
  PREVENTIVA: 'var(--info)',
  REVISION: 'var(--verde)',
  LLEGADA: 'var(--ambar)',
  SALIDA: 'var(--t-navy)',
  OTRO: 'var(--tinta-suave)',
};

/** Las 42 celdas (6 semanas desde el lunes) que forman la rejilla del mes. */
function celdasDelMes(anio: number, mes: number): string[] {
  const primero = new Date(Date.UTC(anio, mes - 1, 1));
  const desplazamiento = (primero.getUTCDay() + 6) % 7;
  const inicio = new Date(Date.UTC(anio, mes - 1, 1 - desplazamiento));
  return Array.from({ length: 42 }, (_, i) =>
    new Date(inicio.getTime() + i * 86_400_000).toISOString().slice(0, 10),
  );
}

function hoyIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Calendario de eventos. La agenda operativa de la gestora: visitas,
 * mantenimiento, revisiones y estancias, todo del backend real. Rejilla mensual
 * de siete columnas desde el lunes; cada evento es un chip con el color de su
 * tipo, y hoy va marcado. Se crean y editan eventos que persisten.
 */
export function Calendario({ idioma, onVolver, onAbrirPropiedad }: Props) {
  const inicial = new Date();
  const [anio, setAnio] = useState(inicial.getUTCFullYear());
  const [mes, setMes] = useState(inicial.getUTCMonth() + 1); // 1-based
  const [propiedadFiltro, setPropiedadFiltro] = useState('todas');
  const [apagados, setApagados] = useState<TipoEvento[]>([]);
  const [eventos, setEventos] = useState<EventoCalendario[]>([]);
  const [propiedades, setPropiedades] = useState<PropiedadResumen[]>([]);
  const [usuarios, setUsuarios] = useState<Array<{ id: string; nombre: string }>>([]);
  const [error, setError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EventoCalendario | 'nuevo' | null>(null);

  const cargar = useCallback(() => {
    api
      .calendario(anio, mes, propiedadFiltro === 'todas' ? undefined : propiedadFiltro)
      .then((r) => setEventos(r.eventos))
      .catch(() => setError(t(idioma, 'error.servidor')));
  }, [anio, mes, propiedadFiltro, idioma]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    api
      .propiedades()
      .then((r) => setPropiedades(r.propiedades))
      .catch(() => undefined);
    api
      .usuarios()
      .then((r) => setUsuarios(r.usuarios.map((u) => ({ id: u.id, nombre: u.nombre }))))
      .catch(() => undefined);
  }, []);

  const visibles = useMemo(
    () => eventos.filter((e) => !apagados.includes(e.tipo)),
    [eventos, apagados],
  );

  const porDia = useMemo(() => {
    const mapa = new Map<string, EventoCalendario[]>();
    for (const e of visibles) {
      const arr = mapa.get(e.fecha) ?? [];
      arr.push(e);
      mapa.set(e.fecha, arr);
    }
    return mapa;
  }, [visibles]);

  const celdas = celdasDelMes(anio, mes);
  const hoy = hoyIso();
  const mesTexto = new Intl.DateTimeFormat(idioma, { month: 'long', year: 'numeric' }).format(
    new Date(Date.UTC(anio, mes - 1, 1)),
  );
  const diasSemana = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(idioma, { weekday: 'narrow' }).format(new Date(Date.UTC(2026, 5, 1 + i))),
  );

  const mover = (paso: number) => {
    const d = new Date(Date.UTC(anio, mes - 1 + paso, 1));
    setAnio(d.getUTCFullYear());
    setMes(d.getUTCMonth() + 1);
  };

  const irHoy = () => {
    const d = new Date();
    setAnio(d.getUTCFullYear());
    setMes(d.getUTCMonth() + 1);
  };

  const alternar = (tipo: TipoEvento) =>
    setApagados((a) => (a.includes(tipo) ? a.filter((x) => x !== tipo) : [...a, tipo]));

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'calendario.titulo')}</h1>
          <p className="mes-actual" style={{ textTransform: 'capitalize' }}>
            {mesTexto}
          </p>
        </div>
        <div className="acciones">
          <div className="conmutador-vista" role="group" aria-label={t(idioma, 'calendario.mes')}>
            <button type="button" onClick={() => mover(-1)} aria-label={t(idioma, 'calendario.anterior')}>
              ‹
            </button>
            <button type="button" onClick={() => mover(1)} aria-label={t(idioma, 'calendario.siguiente')}>
              ›
            </button>
          </div>
          <button type="button" className="secundario" onClick={irHoy}>
            {t(idioma, 'calendario.hoy')}
          </button>
          <select
            aria-label={t(idioma, 'calendario.todas')}
            value={propiedadFiltro}
            onChange={(e) => setPropiedadFiltro(e.target.value)}
            style={{ maxWidth: '14rem' }}
          >
            <option value="todas">{t(idioma, 'calendario.todas')}</option>
            {propiedades.map((p) => (
              <option key={p.id} value={p.id}>
                {p.codigo} · {p.nombre}
              </option>
            ))}
          </select>
          <button type="button" className="secundario" onClick={onVolver}>
            {t(idioma, 'app.volver')}
          </button>
          <button type="button" onClick={() => setEditor('nuevo')}>
            + {t(idioma, 'calendario.nuevo')}
          </button>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <div className="filtros-tipo" role="group" aria-label={t(idioma, 'calendario.titulo')}>
        {TIPOS.map((tipo) => (
          <button
            key={tipo}
            type="button"
            className="chip-tipo"
            aria-pressed={!apagados.includes(tipo)}
            style={{ '--color-tipo': COLOR[tipo] } as React.CSSProperties}
            onClick={() => alternar(tipo)}
          >
            <i />
            {t(idioma, `evento.${tipo}`)}
          </button>
        ))}
      </div>

      {editor && (
        <FormularioEvento
          idioma={idioma}
          evento={editor === 'nuevo' ? null : editor}
          propiedades={propiedades}
          usuarios={usuarios}
          fechaPorDefecto={hoy}
          onGuardado={() => {
            setEditor(null);
            cargar();
          }}
          onCancelar={() => setEditor(null)}
          onEstado={(estado) => {
            if (editor === 'nuevo') return;
            api
              .cambiarEstadoEvento(editor.id, estado)
              .then(() => {
                setEditor(null);
                cargar();
              })
              .catch(() => setError(t(idioma, 'error.servidor')));
          }}
          onError={() => setError(t(idioma, 'error.servidor'))}
        />
      )}

      <div className="tarjeta plana">
        <div className="rejilla-mes" role="grid" aria-label={mesTexto}>
          {diasSemana.map((dia, i) => (
            <div key={i} className="cabecera-dia" role="columnheader">
              {dia.toUpperCase()}
            </div>
          ))}

          {celdas.map((dia) => {
            const delMes = Number(dia.slice(5, 7)) === mes;
            const esHoy = dia === hoy;
            const delDia = porDia.get(dia) ?? [];
            const mostrados = delDia.slice(0, 3);
            const resto = delDia.length - mostrados.length;
            return (
              <div key={dia} className={`celda-dia${delMes ? '' : ' fuera'}`} role="gridcell">
                <span className={`numero-dia${esHoy ? ' hoy' : ''}`}>{Number(dia.slice(8, 10))}</span>
                {mostrados.map((evento) => (
                  <button
                    key={evento.id}
                    type="button"
                    className="chip-evento"
                    style={
                      {
                        '--color-tipo': COLOR[evento.tipo],
                        ...(evento.estado === 'CANCELADA'
                          ? { textDecoration: 'line-through', opacity: 0.55 }
                          : {}),
                      } as React.CSSProperties
                    }
                    onClick={() => setEditor(evento)}
                    title={`${t(idioma, `evento.${evento.tipo}`)} · ${evento.titulo}${
                      evento.propiedadCodigo ? ` · ${evento.propiedadCodigo}` : ''
                    }`}
                  >
                    {evento.hora ? `${evento.hora} ` : ''}
                    {evento.titulo}
                  </button>
                ))}
                {resto > 0 && (
                  <span className="mas-eventos">{t(idioma, 'calendario.mas', { total: resto })}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/*
        LA MISMA INFORMACION, EN AGENDA, PARA EL MOVIL.

        Una rejilla de siete columnas no cabe en 343 pixeles: o se desplaza en
        horizontal —y entonces media semana esta siempre fuera de la pantalla—
        o las celdas quedan tan estrechas que solo cabe el numero del dia, sin
        el evento, que es lo unico que se venia a ver. En un telefono el mes se
        lee como lista, que ademas es lo que hace cualquier agenda de movil. La
        hoja de estilo ensena una u otra segun el ancho; los datos son los
        mismos y no hay una segunda fuente que se pueda desincronizar.
      */}
      <div className="agenda-mes">
        {visibles.length === 0 ? (
          <p className="suave">{t(idioma, 'calendario.sinEventos')}</p>
        ) : (
          [...visibles]
            .sort((a, b) => (a.fecha + (a.hora ?? '')).localeCompare(b.fecha + (b.hora ?? '')))
            .map((evento) => (
              <button
                key={evento.id}
                type="button"
                className="agenda-fila"
                style={{ '--color-tipo': COLOR[evento.tipo] } as React.CSSProperties}
                onClick={() => setEditor(evento)}
              >
                <span className="agenda-dia">
                  <b>{Number(evento.fecha.slice(8, 10))}</b>
                  <small>{evento.hora ?? '—'}</small>
                </span>
                <span className="agenda-que">
                  <b>{evento.titulo}</b>
                  <small>
                    {t(idioma, `evento.${evento.tipo}`)}
                    {evento.propiedadCodigo ? ` · ${evento.propiedadCodigo}` : ''}
                  </small>
                </span>
              </button>
            ))
        )}
      </div>

      {visibles.length === 0 && !editor && (
        <div className="tarjeta rejilla-solo-escritorio">
          <p>{t(idioma, 'calendario.sinEventos')}</p>
        </div>
      )}

      {editor && editor !== 'nuevo' && editor.propiedadId && (
        <p>
          <button
            type="button"
            className="enlace"
            onClick={() => onAbrirPropiedad(editor.propiedadId!)}
          >
            {t(idioma, 'evento.irPropiedad')} ({editor.propiedadCodigo})
          </button>
        </p>
      )}
    </main>
  );
}

function FormularioEvento({
  idioma,
  evento,
  propiedades,
  usuarios,
  fechaPorDefecto,
  onGuardado,
  onCancelar,
  onEstado,
  onError,
}: {
  idioma: Idioma;
  evento: EventoCalendario | null;
  propiedades: PropiedadResumen[];
  usuarios: Array<{ id: string; nombre: string }>;
  fechaPorDefecto: string;
  onGuardado: () => void;
  onCancelar: () => void;
  onEstado: (estado: EstadoEvento) => void;
  onError: () => void;
}) {
  const [tipo, setTipo] = useState<TipoEvento>(evento?.tipo ?? 'VISITA');
  const [titulo, setTitulo] = useState(evento?.titulo ?? '');
  const [fecha, setFecha] = useState(evento?.fecha ?? fechaPorDefecto);
  const [hora, setHora] = useState(evento?.hora ?? '');
  const [propiedadId, setPropiedadId] = useState(evento?.propiedadId ?? '');
  const [asignado, setAsignado] = useState(evento?.asignadoUserId ?? '');
  const [notas, setNotas] = useState(evento?.notas ?? '');
  const [enviando, setEnviando] = useState(false);

  const guardar = (e: React.FormEvent) => {
    e.preventDefault();
    if (titulo.trim().length < 2 || !fecha) return;
    setEnviando(true);
    const limpio = (v: string) => v.trim();

    const promesa = evento
      ? api.editarEvento(evento.id, {
          tipo,
          titulo: limpio(titulo),
          fecha,
          hora: hora ? hora : null,
          propiedadId: propiedadId ? propiedadId : null,
          asignadoUserId: asignado ? asignado : null,
          notas: notas.trim() ? notas.trim() : null,
        } as EditarEvento)
      : api.crearEvento({
          tipo,
          titulo: limpio(titulo),
          fecha,
          ...(hora ? { hora } : {}),
          ...(propiedadId ? { propiedadId } : {}),
          ...(asignado ? { asignadoUserId: asignado } : {}),
          ...(notas.trim() ? { notas: notas.trim() } : {}),
        } as CrearEvento);

    promesa
      .then(onGuardado)
      .catch((err: unknown) => {
        onError();
        if (!(err instanceof ErrorApi)) console.error(err);
      })
      .finally(() => setEnviando(false));
  };

  return (
    <form className="tarjeta formulario" onSubmit={guardar}>
      <div className="rejilla dos">
        <div className="campo">
          <label htmlFor="ev-titulo">{t(idioma, 'evento.titulo')}</label>
          <input
            id="ev-titulo"
            type="text"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            required
            minLength={2}
            maxLength={200}
          />
        </div>
        <div className="campo">
          <label htmlFor="ev-tipo">{t(idioma, 'evento.tipo')}</label>
          <select id="ev-tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoEvento)}>
            {TIPOS.map((x) => (
              <option key={x} value={x}>
                {t(idioma, `evento.${x}`)}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="ev-fecha">{t(idioma, 'evento.fecha')}</label>
          <input id="ev-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} required />
        </div>
        <div className="campo">
          <label htmlFor="ev-hora">{t(idioma, 'evento.hora')}</label>
          <input id="ev-hora" type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="ev-propiedad">{t(idioma, 'evento.propiedad')}</label>
          <select id="ev-propiedad" value={propiedadId} onChange={(e) => setPropiedadId(e.target.value)}>
            <option value="">{t(idioma, 'evento.sinPropiedad')}</option>
            {propiedades.map((p) => (
              <option key={p.id} value={p.id}>
                {p.codigo} · {p.nombre}
              </option>
            ))}
          </select>
        </div>
        <div className="campo">
          <label htmlFor="ev-asignado">{t(idioma, 'evento.asignado')}</label>
          <select id="ev-asignado" value={asignado} onChange={(e) => setAsignado(e.target.value)}>
            <option value="">{t(idioma, 'evento.sinAsignar')}</option>
            {usuarios.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="campo">
        <label htmlFor="ev-notas">{t(idioma, 'evento.notas')}</label>
        <textarea id="ev-notas" rows={2} value={notas} onChange={(e) => setNotas(e.target.value)} maxLength={2000} />
      </div>

      <div className="fila-flex" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
        <button type="submit" disabled={enviando || titulo.trim().length < 2}>
          {t(idioma, 'evento.guardar')}
        </button>
        {evento && evento.estado !== 'HECHA' && (
          <button type="button" className="secundario" onClick={() => onEstado('HECHA')}>
            {t(idioma, 'evento.marcarHecha')}
          </button>
        )}
        {evento && evento.estado !== 'CANCELADA' && (
          <button type="button" className="secundario" onClick={() => onEstado('CANCELADA')}>
            {t(idioma, 'evento.marcarCancelada')}
          </button>
        )}
        {evento && evento.estado !== 'PREVISTA' && (
          <button type="button" className="secundario" onClick={() => onEstado('PREVISTA')}>
            {t(idioma, 'evento.reabrir')}
          </button>
        )}
        <button type="button" className="secundario" onClick={onCancelar}>
          {t(idioma, 'evento.cerrar')}
        </button>
      </div>
    </form>
  );
}
